import { CacheType } from '@descomplica/caching-browser';
import {
  BaseHttpGateway,
  CollaborationResult,
  RecognitionManager,
} from '@descomplica/microfrontend-core';
import { injectable } from 'tsyringe';

import type {
  AnswerDto,
  ExamAnswerDto,
  ExamStartDto,
  IPegasusGateway,
  UserDto,
} from '@/application/interfaces/exercises/pegasus.gateway';
import envs from '@/infrastructure/config/envs';

@injectable()
export class PegasusGateway extends BaseHttpGateway implements IPegasusGateway {
  constructor(private readonly recognitionManager: RecognitionManager) {
    super();
  }

  async getExercisesByGuid(guid: string): Promise<CollaborationResult<any>> {
    const { data } = await this.axios.get(`/exercises/${guid}`);

    return data;
  }

  async getExercisesAnswerByGuid(
    guid: string,
    lessonGuid: string,
  ): Promise<CollaborationResult<any>> {
    const { data } = await this.axios.get(`/exercises/answer/${guid}/lesson/${lessonGuid}`);
    return data;
  }

  async getExamExercises(guid: string): Promise<CollaborationResult<any>> {
    const { data } = await this.axios.get(`/exam/exercises/${guid}`);

    return data;
  }

  async getExamAnswerByGuid(guid: string): Promise<CollaborationResult<any>> {
    const { data } = await this.axios.get(`/exam/answer/${guid}`);
    return data;
  }

  async postExamStart(dto: ExamStartDto): Promise<CollaborationResult<any>> {
    const { data } = await this.axios.post(`/exam/start`, dto);
    return data;
  }

  async setExamFinished(guid: string): Promise<CollaborationResult<any>> {
    const { data } = await this.axios.patch(`/exam/${guid}`, {
      finished: true,
    });
    return data;
  }

  async postAnswerByGuid(dto: ExamAnswerDto): Promise<CollaborationResult<any>> {
    const { data } = await this.axios.post(`/exam/answer`, dto);
    return data;
  }

  async getAnswerByGuid(dto: AnswerDto): Promise<CollaborationResult<any>> {
    const { data } = await this.axios.post(`/exercises/answer`, dto);
    return data;
  }

  async getIdentifiedUser(): Promise<CollaborationResult<UserDto>> {
    const { data } = await this.axios.get(`/identified-user`);
    return data;
  }

  protected getEndpoint(): string {
    return `${envs.pegasusUrl}/application-management`;
  }

  protected getCacheType(): CacheType {
    return CacheType.LOCALSTORAGE;
  }

  protected getCacheNamespace(): string {
    return 'library-pegasus-lms-gateway';
  }

  protected getBearerToken(): string {
    return this.recognitionManager.getAccessToken();
  }

  protected getHeaders(): object {
    return {
      'Cache-Control': 'no-cache',
    };
  }
}
